// Constants for OTR classification
export const OTR_THRESHOLDS = {
  early: -120, // 120 seconds early is considered early
  onTime: 120,  // Up to 120 seconds (2 minutes) late is still on-time
  late: 300,  // 300 seconds (5 minutes) late is considered late
  veryLate: 900, // 900 seconds (15 minutes) late is considered very late
};

// Time periods classification
const TIME_PERIODS = {
  MORNING: {
    label: 'Morning Peak (6-10am)',
    start: '06:00',
    end: '10:00'
  },
  MIDDAY: {
    label: 'Midday (10am-3pm)',
    start: '10:00',
    end: '15:00'
  },
  AFTERNOON: {
    label: 'Afternoon Peak (3-7pm)',
    start: '15:00',
    end: '19:00'
  },
  EVENING: {
    label: 'Evening (7-12am)',
    start: '19:00',
    end: '23:59'
  },
  NIGHT: {
    label: 'Night (12-6am)',
    start: '00:00',
    end: '06:00'
  }
};

/**
 * Parse time string to minutes past midnight
 * @param {string} timeStr - Time string
 * @returns {number} Minutes past midnight
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  
  try {
    // Handle full datetime string (2024-12-31 07:26:00)
    let timePart = timeStr;
    if (timeStr.includes(' ')) {
      timePart = timeStr.split(' ')[1];
    }
    
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    if(isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      return null;
    }
    return (hours * 60) + minutes + (seconds / 60);
  } catch (error) {
    console.warn('Error parsing time string:', timeStr, error);
    return null;
  }
};

/**
 * Calculate the time variance between scheduled and actual times
 * @param {string} scheduled - Scheduled time
 * @param {string} actual - Actual time
 * @returns {number} - Variance in minutes (positive = late, negative = early)
 */
export const calculateTimeVariance = (scheduled, actual) => {
  try {
    if (!scheduled || !actual) {
      return 0;
    }

    // Parse times to minutes since midnight
    const scheduledMinutes = timeToMinutes(scheduled);
    const actualMinutes = timeToMinutes(actual);
    
    if (scheduledMinutes === null || actualMinutes === null) {
      return 0;
    }
    
    // Calculate variance (positive = late, negative = early)
    return actualMinutes - scheduledMinutes;
  } catch (error) {
    console.error('Error calculating time variance:', error.message, { scheduled, actual });
    return 0; // Default to no variance on error
  }
};

/**
 * Determines if a timing point is on time, early, or late
 * @param {number} variance - Time variance in seconds
 * @returns {string} - 'on-time', 'early', 'late', 'very-late', or 'unknown'
 */
export const getTimingStatus = (variance) => {
  if (variance === undefined || variance === null) return 'unknown';
  
  // Convert to number if it's a string
  const varianceInSeconds = typeof variance === 'string' ? parseInt(variance, 10) : variance;
  
  if (isNaN(varianceInSeconds)) return 'unknown';
  
  if (varianceInSeconds <= OTR_THRESHOLDS.early) return 'early';
  if (varianceInSeconds >= OTR_THRESHOLDS.veryLate) return 'very-late';
  if (varianceInSeconds >= OTR_THRESHOLDS.late) return 'late';
  return 'on-time';
};

/**
 * Determine the time period based on a time string
 * @param {string} timeStr - Time string in format HH:MM or HH:MM:SS or full datetime
 * @returns {string} Time period key (MORNING, MIDDAY, AFTERNOON, EVENING, NIGHT) or 'unknown'
 */
export const getTimePeriod = (timeStr) => {
  try {
    if (!timeStr) {
      console.log('No time string provided for getTimePeriod');
      return 'unknown';
    }
    
    // Extract time part if it's a datetime string
    let timePart = timeStr;
    
    // If it contains a space, it might be a datetime string
    if (timeStr.includes(' ')) {
      const parts = timeStr.split(' ');
      timePart = parts[parts.length - 1]; // Take the last part as the time
    }
    
    // Extract hours and minutes
    let hours, minutes;
    
    // Try different formats
    if (timePart.includes(':')) {
      const timeParts = timePart.split(':');
      hours = parseInt(timeParts[0], 10);
      minutes = parseInt(timeParts[1], 10);
    } else if (timePart.length === 4) {
      // Format like "0930"
      hours = parseInt(timePart.substring(0, 2), 10);
      minutes = parseInt(timePart.substring(2, 4), 10);
    } else {
      console.log(`Unrecognized time format: ${timeStr}`);
      return 'unknown';
    }
    
    // Check for valid hours and minutes
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.log(`Invalid time values: hours=${hours}, minutes=${minutes} from ${timeStr}`);
      return 'unknown';
    }
    
    // Format as HH:MM for comparison
    const timeFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    // Determine time period
    for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
      if (timeFormatted >= start && timeFormatted <= end) {
        return period;
      }
    }
    
    console.log(`Time ${timeFormatted} did not match any defined period`);
    return 'unknown';
  } catch (error) {
    console.error('Error in getTimePeriod:', error);
    return 'unknown';
  }
};

/**
 * Identify a stop's position (first, middle, last) based on sequence data
 * @param {Object} record - CSV record
 * @returns {string} - 'first', 'middle', 'last', or 'unknown'
 */
const getStopPosition = (record) => {
  // Using TSN_FIRST_LAST_IND from the data if available
  if (record.TSN_FIRST_LAST_IND) {
    if (record.TSN_FIRST_LAST_IND === 'F') return 'first';
    if (record.TSN_FIRST_LAST_IND === 'L') return 'last';
    return 'middle';
  }
  
  // Alternative logic using sequence numbers if TSN_FIRST_LAST_IND is not reliable
  // Check if there's a sequence value we can use
  const seqStr = record.TIME_RECORD_SEQUENCE;
  if (!seqStr) return 'unknown';
  
  const seq = parseInt(seqStr, 10);
  if (isNaN(seq)) return 'unknown';
  
  // Check special indicators
  if (seq === 1) return 'first';
  
  // Enhanced last stop detection
  // 1. Check if only arrival time exists (no departure)
  if (record.ARRIVAL_TIME_VARIANCE && !record.DEPARTURE_TIME_VARIANCE) {
    return 'last';
  }
  
  // 2. Check if this is the last stop in the sequence
  if (record.isLastStop) {
    return 'last';
  }
  
  // 3. Check if this stop has a special last stop indicator
  if (record.stopPosition === 'last' || record.stopPosition === 'end' || 
      record.stopType === 'last' || record.stopType === 'end') {
    return 'last';
  }
  
  // 4. Check if this is a terminus stop
  if (record.isTerminus || record.isTerminal) {
    return 'last';
  }
  
  // Default to middle
  return 'middle';
};

/**
 * Calculate summary statistics from the OTR data
 * @param {Array} data - Parsed CSV data
 * @returns {Object} - Summary statistics
 */
export const calculateSummaryStatistics = (trackedData, data) => {

  // Filter out untracked records and non-timing points first
  const timingPoints = data.filter(r => r.isTimingPoint);

  // Count records with and without variance data (only for tracked timing points)
  const recordsWithVarianceData = trackedData.filter(r => r.hasVarianceData).length;
  const recordsWithoutVarianceData = trackedData.filter(r => !r.hasVarianceData).length;

  // Group records by trip to check first and last stop tracking - use all data for this
  const tripGroups = {};
  data.forEach(record => {
    if (!record.tripId) return;
    
    // Create a unique key that includes tripId, and start date & time
    const tripKey = `${record.tripId}_${record.tripDate}`;
    
    if (!tripGroups[tripKey]) {
      tripGroups[tripKey] = {
        tripId: record.tripId,
        tripDate: record.tripDate,
        scheduledTime: record.scheduledTime,
        firstStopTracked: false,
        lastStopTracked: false
      };
    }
    
    // Add trackedDeparture and trackedArrival fields
    const dep = record.actualDepartureTime;
    const arr = record.actualArrivalTime;
    
    record.trackedDeparture = !!dep && dep !== '';
    record.trackedArrival = !!arr && arr !== '';
    
    // Check if this is a first or last stop
    if (record.stopPosition === 'first') {
      tripGroups[tripKey].firstStopTracked = record.trackedDeparture;
    } else if (record.stopPosition === 'last') {
      tripGroups[tripKey].lastStopTracked = record.trackedArrival;
    }
  });

  // Count trips tracked at both ends - using all data
  const totalTrips = Object.keys(tripGroups).length;
  const fullyTrackedTrips = Object.values(tripGroups).filter(trip => 
    trip.firstStopTracked && trip.lastStopTracked
  ).length;
  const fullyTrackedTripsPercentage = totalTrips > 0 ? (fullyTrackedTrips / totalTrips) * 100 : 0;

  // Count tracked/untracked records based on actual times
  const trackedRecords = trackedData.length;
  const trackedArrivalRecords = trackedData.filter(r => r.trackedArrival).length;
  const untrackedArrivalRecords = trackedData.filter(r => !r.trackedArrival).length;
  const trackedArrivalPercentage = trackedData.length > 0 ? (trackedArrivalRecords / trackedData.length) * 100 : 0;

  // Filter for tracked records with variance data
  const trackedRecordsWithVariance = trackedData.filter(r => r.hasVarianceData && (r.trackedDeparture || r.trackedArrival));
  // Filter for tracked arrival records with variance data
  const trackedArrivalRecordsWithVariance = trackedData.filter(r => r.hasVarianceData && r.trackedArrival);
  
  // Calculate timing statistics for all tracked records with variance data
  const onTimeCount = trackedData.filter(r => r.hasVarianceData && getTimingStatus(r.varianceInSeconds) === 'on-time').length;
  const earlyCount = trackedData.filter(r => r.hasVarianceData && getTimingStatus(r.varianceInSeconds) === 'early').length;
  const lateCount = trackedData.filter(r => r.hasVarianceData && getTimingStatus(r.varianceInSeconds) === 'late').length;
  const veryLateCount = trackedData.filter(r => r.hasVarianceData && getTimingStatus(r.varianceInSeconds) === 'very-late').length;

  // Calculate timing statistics only for tracked records with variance data
  const trackedOnTimeCount = trackedRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'on-time').length;
  const trackedEarlyCount = trackedRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'early').length;
  const trackedLateCount = trackedRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'late').length;
  const trackedVeryLateCount = trackedRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'very-late').length;

  // Calculate timing statistics only for tracked ARRIVAL records with variance data
  const trackedArrivalOnTimeCount = trackedArrivalRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'on-time').length;
  const trackedArrivalEarlyCount = trackedArrivalRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'early').length;
  const trackedArrivalLateCount = trackedArrivalRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'late').length;
  const trackedArrivalVeryLateCount = trackedArrivalRecordsWithVariance.filter(r => getTimingStatus(r.varianceInSeconds) === 'very-late').length;

  // Calculate percentages based on all tracked records with variance data
  const totalWithVariance = recordsWithVarianceData;
  const onTimePercentage = totalWithVariance > 0 ? (onTimeCount / totalWithVariance) * 100 : 0;
  const earlyPercentage = totalWithVariance > 0 ? (earlyCount / totalWithVariance) * 100 : 0;
  const latePercentage = totalWithVariance > 0 ? (lateCount / totalWithVariance) * 100 : 0;
  const veryLatePercentage = totalWithVariance > 0 ? (veryLateCount / totalWithVariance) * 100 : 0;

  // Calculate percentages based on tracked records with variance data
  const totalTrackedWithVariance = trackedRecordsWithVariance.length;
  const trackedOnTimePercentage = totalTrackedWithVariance > 0 ? (trackedOnTimeCount / totalTrackedWithVariance) * 100 : 0;
  const trackedEarlyPercentage = totalTrackedWithVariance > 0 ? (trackedEarlyCount / totalTrackedWithVariance) * 100 : 0;
  const trackedLatePercentage = totalTrackedWithVariance > 0 ? (trackedLateCount / totalTrackedWithVariance) * 100 : 0;
  const trackedVeryLatePercentage = totalTrackedWithVariance > 0 ? (trackedVeryLateCount / totalTrackedWithVariance) * 100 : 0;

  // Calculate percentages based on tracked ARRIVAL records with variance data
  const totalTrackedArrivalWithVariance = trackedArrivalRecordsWithVariance.length;
  const trackedArrivalOnTimePercentage = totalTrackedArrivalWithVariance > 0 ? (trackedArrivalOnTimeCount / totalTrackedArrivalWithVariance) * 100 : 0;
  const trackedArrivalEarlyPercentage = totalTrackedArrivalWithVariance > 0 ? (trackedArrivalEarlyCount / totalTrackedArrivalWithVariance) * 100 : 0;
  const trackedArrivalLatePercentage = totalTrackedArrivalWithVariance > 0 ? (trackedArrivalLateCount / totalTrackedArrivalWithVariance) * 100 : 0;
  const trackedArrivalVeryLatePercentage = totalTrackedArrivalWithVariance > 0 ? (trackedArrivalVeryLateCount / totalTrackedArrivalWithVariance) * 100 : 0;

  // Calculate overall tracked percentage
  const trackedPercentage = timingPoints.length > 0 ? (trackedData.length / timingPoints.length) * 100 : 0;
    
  return {
    totalRecords: data.length,
    noTimingPoints: timingPoints.length,
    recordsWithVarianceData,
    recordsWithoutVarianceData,
    trackedRecords,
    trackedPercentage,
    untrackedArrivalRecords,
    trackedArrivalRecords,
    trackedArrivalPercentage,
    onTimePercentage,
    earlyPercentage,
    latePercentage,
    veryLatePercentage,
    onTimeCount,
    earlyCount,
    lateCount,
    veryLateCount,
    trackedOnTimePercentage,
    trackedEarlyPercentage,
    trackedLatePercentage,
    trackedVeryLatePercentage,
    trackedOnTimeCount,
    trackedEarlyCount,
    trackedLateCount,
    trackedVeryLateCount,
    trackedArrivalOnTimePercentage,
    trackedArrivalEarlyPercentage,
    trackedArrivalLatePercentage,
    trackedArrivalVeryLatePercentage,
    trackedArrivalOnTimeCount,
    trackedArrivalEarlyCount,
    trackedArrivalLateCount,
    trackedArrivalVeryLateCount,
    fullyTrackedTrips,
    totalTrips,
    fullyTrackedTripsPercentage
  };
};

/**
 * Calculate OTR analysis by stop type
 * @param {Array} data - Parsed CSV data
 * @returns {Object} - OTR analysis by stop type
 */
export const calculateOTRAnalysis = (trackedData) => {
  try {
    // Filter out untracked records and non-timing points first
    // const trackedData = data.filter(record => record.tracked && record.isTimingPoint);
    
    // Filter out records without variance data
    const recordsWithVariance = trackedData.filter(record => record.hasVarianceData === true);
    
    if (recordsWithVariance.length === 0) {
      console.warn('No records with variance data for OTR analysis');
      return {
        byStopType: [],
        overall: { otrPercentage: 0 }
      };
    }
    
    // Group by stop position
    const stopPositions = {
      first: recordsWithVariance.filter(record => record.stopPosition === 'first'),
      middle: recordsWithVariance.filter(record => record.stopPosition === 'middle'),
      last: recordsWithVariance.filter(record => record.stopPosition === 'last')
    };
    
    console.log('Stop position counts:', {
      first: stopPositions.first.length,
      middle: stopPositions.middle.length,
      last: stopPositions.last.length,
      total: recordsWithVariance.length
    });
    
    // Calculate OTR for each stop position
    const analysis = [];
    
    // Process each stop position
    for (const [position, records] of Object.entries(stopPositions)) {
      if (records.length === 0) continue;
      
      // Count by timing status
      const onTimeCount = records.filter(r => r.timingStatus === 'on-time').length;
      const earlyCount = records.filter(r => r.timingStatus === 'early').length;
      const lateCount = records.filter(r => r.timingStatus === 'late').length;
      const veryLateCount = records.filter(r => r.timingStatus === 'very-late').length;
      const unknownCount = records.filter(r => r.timingStatus === 'unknown').length;
      
      // Calculate percentages
      const validRecords = records.length - unknownCount;
      const onTimePercentage = validRecords > 0 ? (onTimeCount / validRecords) * 100 : 0;
      const earlyPercentage = validRecords > 0 ? (earlyCount / validRecords) * 100 : 0;
      const latePercentage = validRecords > 0 ? (lateCount / validRecords) * 100 : 0;
      const veryLatePercentage = validRecords > 0 ? (veryLateCount / validRecords) * 100 : 0;
      
      analysis.push({
        stopType: position,
        totalStops: records.length,
        onTimeCount,
        earlyCount,
        lateCount,
        veryLateCount,
        unknownCount,
        onTimePercentage,
        earlyPercentage,
        latePercentage,
        veryLatePercentage
      });
    }
    
    // Calculate overall OTR
    const totalValidRecords = recordsWithVariance.length - recordsWithVariance.filter(r => r.timingStatus === 'unknown').length;
    const overallOnTimeCount = recordsWithVariance.filter(r => r.timingStatus === 'on-time').length;
    const overallOTR = totalValidRecords > 0 ? (overallOnTimeCount / totalValidRecords) * 100 : 0;
    
    return {
      byStopType: analysis,
      overall: {
        otrPercentage: overallOTR,
        totalRecords: recordsWithVariance.length,
        onTimeCount: overallOnTimeCount
      }
    };
  } catch (error) {
    console.error('Error calculating OTR analysis:', error);
    return {
      byStopType: [],
      overall: { otrPercentage: 0 }
    };
  }
};

/**
 * Calculate route performance analysis
 * @param {Array} data - Parsed CSV data
 * @returns {Array} - Route performance data
 */
export const calculateRoutePerformance = (trackedData) => {
  try {
    // Filter out untracked records and non-timing points first
    // const trackedData = data.filter(record => record.tracked && record.isTimingPoint);
    
    // Group data by route
    const routeGroups = {};
    
    trackedData.forEach(record => {
      if (!record.routeNo) return;
      
      const routeNo = record.routeNo;
      if (!routeGroups[routeNo]) {
        routeGroups[routeNo] = {
          route: routeNo,
          totalStops: 0,
          onTimeCount: 0,
          earlyCount: 0,
          lateCount: 0,
          veryLateCount: 0,
          unknownCount: 0,
          totalVariance: 0,
          varianceCount: 0,
          totalTrips: new Set(),
          first: { total: 0, onTime: 0 },
          middle: { total: 0, onTime: 0 },
          last: { total: 0, onTime: 0 }
        };
      }
      
      const group = routeGroups[routeNo];
      
      // Add trip to set of unique trips
      if (record.tripId) {
        group.totalTrips.add(record.tripId);
      }
      
      // Increment total stops
      group.totalStops++;
      
      // Determine stop position
      let position = 'middle';
      if (record.stopPosition === 'first') position = 'first';
      else if (record.stopPosition === 'last') position = 'last';
      
      // Increment position-specific counters
      group[position].total++;
      
      // Calculate timing status
      if (record.hasVarianceData) {
        const timingStatus = getTimingStatus(record.varianceInSeconds);
        
        switch (timingStatus) {
          case 'on-time':
            group.onTimeCount++;
            group[position].onTime++;
            break;
          case 'early':
            group.earlyCount++;
            break;
          case 'late':
            group.lateCount++;
            break;
          case 'very-late':
            group.veryLateCount++;
            break;
          default:
            group.unknownCount++;
        }
        
        // Add to variance calculations
        group.totalVariance += record.varianceInSeconds;
        group.varianceCount++;
      } else {
        group.unknownCount++;
      }
    });
    
    // Calculate route performance metrics
    const routes = Object.values(routeGroups).map(group => {
      const totalValidStops = group.totalStops - group.unknownCount;
      
      // Calculate OTR percentages for each stop type
      const firstStopOTR = group.first.total > 0 
        ? (group.first.onTime / group.first.total) * 100 
        : 0;
      const middleStopOTR = group.middle.total > 0 
        ? (group.middle.onTime / group.middle.total) * 100 
        : 0;
      const lastStopOTR = group.last.total > 0 
        ? (group.last.onTime / group.last.total) * 100 
        : 0;

      return {
        route: group.route,
        totalTrips: group.totalTrips.size,
        totalStops: group.totalStops,
        onTimeCount: group.onTimeCount,
        earlyCount: group.earlyCount,
        lateCount: group.lateCount,
        veryLateCount: group.veryLateCount,
        overallOTR: totalValidStops > 0 ? (group.onTimeCount / totalValidStops) * 100 : 0,
        earlyPercentage: totalValidStops > 0 ? (group.earlyCount / totalValidStops) * 100 : 0,
        latePercentage: totalValidStops > 0 ? (group.lateCount / totalValidStops) * 100 : 0,
        veryLatePercentage: totalValidStops > 0 ? (group.veryLateCount / totalValidStops) * 100 : 0,
        averageVariance: group.varianceCount > 0 ? Math.round(group.totalVariance / group.varianceCount) : 0,
        // Add OTR values for each stop type
        firstStopOTR,
        middleStopOTR,
        lastStopOTR,
        // Add counts for each stop type
        firstStopCount: group.first.total,
        middleStopCount: group.middle.total,
        lastStopCount: group.last.total
      };
    }).sort((a, b) => b.overallOTR - a.overallOTR);
    
    return { routes };
  } catch (error) {
    console.error('Error calculating route performance:', error);
    return { routes: [], error: error.message };
  }
};

/**
 * Calculate time of day analysis
 * @param {Array} data - Parsed CSV data
 * @returns {Object} - Time period analysis
 */
export const calculateTimeOfDayAnalysis = (trackedData) => {
  try {
    // Filter out records without variance data
    const recordsWithVariance = trackedData.filter(record => record.hasVarianceData === true);
    
    if (recordsWithVariance.length === 0) {
      console.warn('No records with variance data for time of day analysis');
      return { periods: [] };
    }
    
    // Define time periods with labels
    const timeGroups = {
      MORNING: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Morning Peak (6-10am)' },
      MIDDAY: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Midday (10am-3pm)' },
      AFTERNOON: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Afternoon Peak (3-7pm)' },
      EVENING: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Evening (7-12am)' },
      NIGHT: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Night (12-6am)' },
      unknown: { totalStops: 0, onTimeCount: 0, earlyCount: 0, lateCount: 0, veryLateCount: 0, label: 'Unknown' }
    };
    
    // Group records by time period
    recordsWithVariance.forEach(record => {
      const period = record.timePeriod || 'unknown';
      
      if (!timeGroups[period]) {
        timeGroups[period] = { 
          totalStops: 0, 
          onTimeCount: 0, 
          earlyCount: 0, 
          lateCount: 0, 
          veryLateCount: 0, 
          label: period 
        };
      }
      
      timeGroups[period].totalStops++;
      
      if (record.timingStatus === 'on-time') {
        timeGroups[period].onTimeCount++;
      } else if (record.timingStatus === 'early') {
        timeGroups[period].earlyCount++;
      } else if (record.timingStatus === 'late') {
        timeGroups[period].lateCount++;
      } else if (record.timingStatus === 'very-late') {
        timeGroups[period].veryLateCount++;
      }
    });
    
    // Calculate percentages and format results
    const periods = Object.entries(timeGroups)
      .filter(([_, group]) => group.totalStops > 0)  // Only include periods with data
      .map(([period, group]) => ({
        period,
        label: group.label,
        totalStops: group.totalStops,
        onTimeCount: group.onTimeCount,
        earlyCount: group.earlyCount,
        lateCount: group.lateCount,
        veryLateCount: group.veryLateCount,
        otrPercentage: (group.onTimeCount / group.totalStops) * 100 || 0,
        earlyPercentage: (group.earlyCount / group.totalStops) * 100 || 0,
        latePercentage: (group.lateCount / group.totalStops) * 100 || 0,
        veryLatePercentage: (group.veryLateCount / group.totalStops) * 100 || 0
      }));
    
    return { periods };
  } catch (error) {
    console.error('Error calculating time of day analysis:', error);
    return { periods: [], error: error.message };
  }
};

/**
 * Calculate driver performance metrics from parsed CSV data
 * @param {Array} parsedData - Array of parsed CSV records
 * @returns {Object} Object containing driver performance analytics
 */
export const calculateDriverPerformance = (trackedData) => {
  try {
    // Filter out untracked records and non-timing points first
    // const trackedData = data.filter(record => record.tracked && record.isTimingPoint);
    
    // Group data by driver
    const driverGroups = {};
    
    trackedData.forEach(record => {
      if (!record.driverId) return;
      
      const driverId = record.driverId;
      if (!driverGroups[driverId]) {
        driverGroups[driverId] = {
          driverId,
          totalStops: 0,
          onTimeCount: 0,
          earlyCount: 0,
          lateCount: 0,
          veryLateCount: 0,
          unknownCount: 0,
          totalVariance: 0,
          varianceCount: 0,
          trips: new Set(),
          routes: new Set(),
          first: { total: 0, onTime: 0 },
          middle: { total: 0, onTime: 0 },
          last: { total: 0, onTime: 0 }
        };
      }
      
      const group = driverGroups[driverId];
      
      // Add trip to set of unique trips
      if (record.tripId) {
        group.trips.add(record.tripId);
      }
      
      // Add route to set of unique routes
      if (record.routeNo) {
        group.routes.add(record.routeNo);
      }
      
      // Increment total stops
      group.totalStops++;
      
      // Determine stop position
      let position = 'middle';
      if (record.stopPosition === 'first') position = 'first';
      else if (record.stopPosition === 'last') position = 'last';
      
      // Increment position-specific counters
      group[position].total++;
      
      // Calculate timing status
      if (record.hasVarianceData) {
        const timingStatus = getTimingStatus(record.varianceInSeconds);
        
        switch (timingStatus) {
          case 'on-time':
            group.onTimeCount++;
            group[position].onTime++;
            break;
          case 'early':
            group.earlyCount++;
            break;
          case 'late':
            group.lateCount++;
            break;
          case 'very-late':
            group.veryLateCount++;
            break;
          default:
            group.unknownCount++;
        }
        
        // Add to variance calculations
        group.totalVariance += record.varianceInSeconds;
        group.varianceCount++;
      } else {
        group.unknownCount++;
      }
    });
    
    // Calculate driver performance metrics
    const drivers = Object.values(driverGroups).map(data => {
      const totalValidStops = data.totalStops - data.unknownCount;
      
      // Calculate OTR percentages for each stop type
      const firstStopOTR = data.first.total > 0 
        ? (data.first.onTime / data.first.total) * 100 
        : 0;
      const middleStopOTR = data.middle.total > 0 
        ? (data.middle.onTime / data.middle.total) * 100 
        : 0;
      const lastStopOTR = data.last.total > 0 
        ? (data.last.onTime / data.last.total) * 100 
        : 0;

      return {
        driverId: data.driverId,
        totalTrips: data.trips.size,
        uniqueRoutes: data.routes.size,
        totalStops: data.totalStops,
        onTimeCount: data.onTimeCount,
        earlyCount: data.earlyCount,
        lateCount: data.lateCount,
        veryLateCount: data.veryLateCount,
        overallOTR: totalValidStops > 0 ? (data.onTimeCount / totalValidStops) * 100 : 0,
        earlyPercentage: totalValidStops > 0 ? (data.earlyCount / totalValidStops) * 100 : 0,
        latePercentage: totalValidStops > 0 ? (data.lateCount / totalValidStops) * 100 : 0,
        veryLatePercentage: totalValidStops > 0 ? (data.veryLateCount / totalValidStops) * 100 : 0,
        averageVariance: data.varianceCount > 0 ? Math.round(data.totalVariance / data.varianceCount) : 0,
        // Add OTR values for each stop type
        firstStopOTR,
        middleStopOTR,
        lastStopOTR,
        // Add counts for each stop type
        firstStopCount: data.first.total,
        middleStopCount: data.middle.total,
        lastStopCount: data.last.total
      };
    }).sort((a, b) => b.overallOTR - a.overallOTR);
    
    return { drivers };
  } catch (error) {
    console.error('Error calculating driver performance:', error);
    return { drivers: [], error: error.message };
  }
};

/**
 * Calculate advanced insights from OTR data
 * @param {Array} data - Processed OTR data
 * @returns {Object} Object containing advanced insights
 */
export const calculateAdvancedInsights = (trackedData) => {
  
  try {
    // Filter out records without variance data
    const recordsWithVariance = trackedData.filter(record => record.hasVarianceData === true);
    
    if (recordsWithVariance.length === 0) {
      console.warn('No records with variance data for advanced insights analysis');
      return { tripPatterns: [], correlations: [] };
    }
    
    // Placeholder for advanced insights
    // This function can be expanded in the future to include more sophisticated analysis
    
    return {
      tripPatterns: [],
      correlations: []
    };
  } catch (error) {
    console.error('Error calculating advanced insights:', error);
    return { tripPatterns: [], correlations: [], error: error.message };
  }
};

/**
 * Calculate proposed revised timetables based on historical performance
 * @param {Array} data - Parsed CSV data
 * @returns {Object} - Proposed timetable adjustments and shift envelope analysis
 */
export const calculateProposedTimetables = (trackedData) => {
  
  try {
    console.log(`Calculating proposed timetables for ${trackedData.length} records`);
    
    // Filter out records without variance data
    const recordsWithVariance = trackedData.filter(record => record.hasVarianceData === true);
    
    if (recordsWithVariance.length === 0) {
      console.warn('No records with variance data for timetable analysis');
      return {
        proposedTimetables: [],
        shiftEnvelope: []
      };
    }
    
    // Group data by route, trip, and stop
    const routeStopGroups = {};
    
    recordsWithVariance.forEach(record => {
      if (!record.routeNo || !record.stopId || !record.tripId) return;
      
      const key = `${record.routeNo}-${record.tripId}-${record.stopId}`;
      
      if (!routeStopGroups[key]) {
        routeStopGroups[key] = {
          routeNo: record.routeNo,
          tripId: record.tripId,
          stopId: record.stopId,
          stopName: record.stopName || `Stop ${record.stopId}`,
          scheduledTime: record.scheduledTime,
          variances: [],
          variancesMinutes: [], // Store variances in minutes for OTR window logic
          shiftIds: new Set()
        };
      }
      
      // Add variance data
      if (record.varianceInSeconds !== null) {
        routeStopGroups[key].variances.push(record.varianceInSeconds);
        routeStopGroups[key].variancesMinutes.push(record.varianceInSeconds / 60);
      }
      
      // Track unique shifts
      if (record.runId) routeStopGroups[key].shiftIds.add(record.runId);
    });
    
    // Calculate proposed adjustments for each route-stop
    const proposedTimetables = Object.values(routeStopGroups).map(group => {
      // Skip if not enough data
      if (group.variances.length < 5) {
        return {
          routeNo: group.routeNo,
          stopId: group.stopId,
          stopName: group.stopName,
          scheduledTime: group.scheduledTime,
          currentOTR: 0,
          proposedAdjustments: [],
          insufficientData: true,
          tripId: group.tripId
        };
      }
      
      // Calculate current OTR percentage
      const onTimeCount = group.variances.filter(v => v >= OTR_THRESHOLDS.early && v <= OTR_THRESHOLDS.onTime).length;
      const currentOTR = (onTimeCount / group.variances.length) * 100;
      
      // Calculate standard deviation
      const mean = group.variances.reduce((sum, v) => sum + v, 0) / group.variances.length;
      const variance = group.variances.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / group.variances.length;
      const stdDev = Math.sqrt(variance);
      
      // Calculate percentiles for 95% and 98% on-time performance
      // For 95% on-time, we need to adjust to the 5th percentile
      // For 98% on-time, we need to adjust to the 2nd percentile
      const percentile95 = mean - (1.645 * stdDev);
      const percentile98 = mean - (2.054 * stdDev);
      
      // Calculate proposed adjustments in seconds
      // If the percentile is negative, we need to add time (move schedule later)
      // If the percentile is positive, we need to subtract time (move schedule earlier)
      const adjustment95 = -percentile95;
      const adjustment98 = -percentile98;
      
      // Convert to minutes for display
      const adjustment95Minutes = Math.round(adjustment95 / 60);
      const adjustment98Minutes = Math.round(adjustment98 / 60);
      
      // Calculate projected OTR after adjustments
      const projectedOTR95 = group.variances.filter(v => (v + adjustment95) >= OTR_THRESHOLDS.early && (v + adjustment95) <= OTR_THRESHOLDS.onTime).length / group.variances.length * 100;
      const projectedOTR98 = group.variances.filter(v => (v + adjustment98) >= OTR_THRESHOLDS.early && (v + adjustment98) <= OTR_THRESHOLDS.onTime).length / group.variances.length * 100;
      
      return {
        routeNo: group.routeNo,
        stopId: group.stopId,
        stopName: group.stopName,
        scheduledTime: group.scheduledTime,
        currentOTR,
        stdDev,
        meanVariance: mean, // in seconds
        variances: group.variancesMinutes, // Add variances in minutes for OTR window logic
        proposedAdjustments: [
          {
            target: 95,
            adjustmentSeconds: adjustment95,
            adjustmentMinutes: adjustment95Minutes,
            projectedOTR: projectedOTR95
          },
          {
            target: 98,
            adjustmentSeconds: adjustment98,
            adjustmentMinutes: adjustment98Minutes,
            projectedOTR: projectedOTR98
          }
        ],
        tripCount: 1,
        shiftCount: group.shiftIds.size,
        insufficientData: false,
        tripId: group.tripId
      };
    }).filter(item => !item.insufficientData);
    
    // Calculate shift envelope analysis
    const shiftEnvelope = calculateShiftEnvelope(proposedTimetables, trackedData);
    
    return {
      proposedTimetables,
      shiftEnvelope
    };
  } catch (error) {
    console.error('Error calculating proposed timetables:', error);
    return {
      error: `Failed to calculate proposed timetables: ${error.message}`,
      proposedTimetables: [],
      shiftEnvelope: []
    };
  }
};

/**
 * Calculate shift envelope analysis based on proposed timetable adjustments
 * @param {Array} proposedTimetables - Proposed timetable adjustments
 * @param {Array} data - Original parsed data
 * @returns {Array} - Shift envelope analysis
 */
const calculateShiftEnvelope = (proposedTimetables, trackedData) => {
  try {
    // Group data by shift (runId)
    const shiftGroups = {};
    
    trackedData.forEach(record => {
      if (!record.runId) return;
      
      if (!shiftGroups[record.runId]) {
        shiftGroups[record.runId] = {
          shiftId: record.runId,
          routeNo: record.routeNo,
          trips: new Set(),
          stops: new Set(),
          totalDuration: 0,
          adjustments: {
            '95': { totalAdjustment: 0, affectedStops: 0 },
            '98': { totalAdjustment: 0, affectedStops: 0 }
          }
        };
      }
      
      shiftGroups[record.runId].trips.add(record.tripId);
      shiftGroups[record.runId].stops.add(`${record.routeNo}-${record.stopId}`);
    });
    
    // Calculate adjustments for each shift
    Object.values(shiftGroups).forEach(shift => {
      // Find all stops in this shift that have proposed adjustments
      const affectedStops = proposedTimetables.filter(proposal => 
        shift.stops.has(`${proposal.routeNo}-${proposal.stopId}`)
      );
      
      // Calculate total adjustment for each target percentage
      affectedStops.forEach(stop => {
        stop.proposedAdjustments.forEach(adjustment => {
          const target = adjustment.target.toString();
          shift.adjustments[target].totalAdjustment += adjustment.adjustmentSeconds;
          shift.adjustments[target].affectedStops++;
        });
      });
      
      // Convert to minutes for display
      shift.adjustments['95'].totalAdjustmentMinutes = Math.round(shift.adjustments['95'].totalAdjustment / 60);
      shift.adjustments['98'].totalAdjustmentMinutes = Math.round(shift.adjustments['98'].totalAdjustment / 60);
    });
    
    // Convert to array and sort by impact
    return Object.values(shiftGroups)
      .map(shift => ({
        shiftId: shift.shiftId,
        routeNo: shift.routeNo,
        tripCount: shift.trips.size,
        stopCount: shift.stops.size,
        adjustments: shift.adjustments
      }))
      .sort((a, b) => Math.abs(b.adjustments['95'].totalAdjustment) - Math.abs(a.adjustments['95'].totalAdjustment));
  } catch (error) {
    console.error('Error calculating shift envelope:', error);
    return [];
  }
};

/**
 * Generate a comprehensive OTR report from parsed CSV data
 * @param {Array} parsedData - Array of parsed CSV records
 * @returns {Object} Object containing all OTR analytics
 */
export const generateOTRReport = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return {
      error: 'No data provided for report generation',
      routePerformance: {routes:[]},
      driverPerformance: {drivers:[]},
      summary: { totalRecords: 0 }
    };
  }
  
  try {
    console.log(`Generating OTR report for ${data.length} records`);
    
    const trackedData = data.filter(r => r.tracked && r.isTimingPoint);
  
    // Calculate summary statistics
    const summary = calculateSummaryStatistics(trackedData, data);
    
    // Calculate OTR analysis by stop type
    const otrAnalysis = calculateOTRAnalysis(trackedData);
    
    // Calculate route performance
    const routePerformance = calculateRoutePerformance(trackedData);
    
    // Calculate time of day analysis
    const timeOfDayAnalysis = calculateTimeOfDayAnalysis(trackedData);
    
    // Calculate driver performance
    const driverPerformance = calculateDriverPerformance(trackedData);
    
    // Calculate advanced insights
    const advancedInsights = calculateAdvancedInsights(trackedData);
    
    // Calculate proposed revised timetables
    const proposedTimetables = calculateProposedTimetables(trackedData);
    
    // Log report generation summary
    console.log('OTR Report Generation Summary:');
    console.log(`- Total records: ${summary.totalRecords}`);
    console.log(`- Records with variance data: ${summary.recordsWithVarianceData}`);
    console.log(`- Records without variance data: ${summary.recordsWithoutVarianceData}`);
    console.log(`- OTR percentage: ${summary.onTimePercentage.toFixed(1)}%`);
    console.log(`- Routes analyzed: ${routePerformance.routes.length}`);
    console.log(`- Driver records: ${driverPerformance.drivers.length}`);
    console.log(`- Proposed timetable stops: ${proposedTimetables.proposedTimetables.length}`);
    
    // Return the complete report
    return {
      summary,
      otrAnalysis,
      routePerformance,
      timeOfDayAnalysis,
      driverPerformance,
      advancedInsights,
      proposedTimetables
    };
  } catch (error) {
    console.error('Error generating OTR report:', error);
    return {
      error: `Failed to generate report: ${error.message}`,
      summary: { totalRecords: data.length },
      otrAnalysis: { byStopType: [], overall: { otrPercentage: 0 } },
      routePerformance: { routes: [] },
      timeOfDayAnalysis: { periods: [] },
      driverPerformance: { drivers: [] },
      advancedInsights: { tripPatterns: [], correlations: [] },
      proposedTimetables: { proposedTimetables: [], shiftEnvelope: [] }
    };
  }
}; 